import { checkboxField } from './form/checkbox'
import { descriptionField } from './form/description'
import { fileField } from './form/file'
import { radioField } from './form/radio'
import { textField } from './form/text'
import { textareaField } from './form/textarea'
import type { Facets, Vacancy } from './types/vacancies'
import { h, wrapInParagraph } from './utils'

interface RenderVacancyOptions {
  tags?: (keyof Facets)[]
}

export async function renderVacancy(
  vacancy: Vacancy,
  container: HTMLElement,
  options: RenderVacancyOptions = {
    tags: ['Locations', 'EmploymentTypes', 'WorkLocationPreferences'],
  },
) {
  // Header
  const header = h('div', { class: 'vbzc-vacancy-header' }, { parent: container })

  // Title
  h('p', { class: 'vbzc-vacancy-title vbzc-h3' }, { parent: header, text: vacancy.Title })

  // Tags
  if (options.tags?.length) {
    const tags = h('div', { class: 'vbzc-vacancy-tags vbzc-tags' }, { parent: header })

    for (const tag of options.tags) {
      for (const { Name } of vacancy.Facets[tag] ?? []) {
        h('span', { class: 'vbzc-tag' }, { text: Name, parent: tags })
      }
    }
  }

  // Columns
  const columns = h('div', { class: 'vbzc-vacancy-columns' }, { parent: container })
  const mainColumn = h('div', { class: 'vbzc-vacancy-main-column' }, { parent: columns })
  const sideColumn = h('div', { class: 'vbzc-vacancy-side-column' }, { parent: columns })

  // Description
  h(
    'div',
    { class: 'vbzc-vacancy-description vbzc-prose' },
    { parent: mainColumn, html: prepareVacancyDescription(vacancy) },
  )

  // Application form
  const application = h(
    'div',
    { class: 'vbzc-vacancy-application' },
    {
      parent: mainColumn,
      children: [h('h2', { class: 'vbzc-h3' }, { text: ZC_APPLICATION_TITLE }), await vacancyForm(vacancy.Id)],
    },
  )

  // Recruiter
  if (vacancy.Descriptions.AdditionalDescription) {
    const recruiter = h(
      'div',
      { class: 'vbzc-vacancy-recruiter vbzc-prose' },
      { parent: sideColumn, html: wrapInParagraph(vacancy.Descriptions.AdditionalDescription) },
    )

    application.prepend(recruiter.cloneNode(true))
  }

  // Application button
  const applicationButton = h(
    'button',
    { class: 'vbzc-vacancy-cta vbzc-button vbzc-full-width', type: 'button' },
    { parent: sideColumn, text: ZC_APPLY_LABEL },
  )
  applicationButton.addEventListener('click', () => {
    const focusable = application.querySelector('input, select, textarea') as HTMLElement | null
    application.scrollIntoView({ behavior: 'instant', block: 'center' })
    focusable?.focus()
  })
}

function prepareVacancyDescription(vacancy: Vacancy) {
  let description = ''

  for (const descriptionKey of [
    'Summary',
    'FunctionDescription',
    'ClientDescription',
    'RequirementsDescription',
    'OfferDescription',
  ] as const) {
    const titleKey = `${descriptionKey}Title` as const

    if (titleKey && vacancy.Descriptions[titleKey]) {
      description += `<p class="vbzc-h4">${vacancy.Descriptions[titleKey]}</p>`
    }

    if (descriptionKey && vacancy.Descriptions[descriptionKey]) {
      description += wrapInParagraph(vacancy.Descriptions[descriptionKey])
    }
  }

  return description
}

export async function vacancyForm(vacancyId: string) {
  const fields: HTMLElement[] = []
  const customFields: { fieldName: string; required: boolean }[] = ZC_VACANCY_FORM_FIELDS
    ? JSON.parse(ZC_VACANCY_FORM_FIELDS).map((fieldName: string) =>
        fieldName.endsWith('*')
          ? { fieldName: fieldName.slice(0, -1), required: true }
          : { fieldName, required: false },
      )
    : []
  const requiredFields = customFields.filter(({ required }) => required)
  const filteredFields: HTMLElement[] = []
  const data = await fetch(
    `https://${ZC_HANDLE}.cockpit.recruitnow.${ZC_DOMAIN}/jobsite/api/applicationforms/${vacancyId}?formId=ApplicationForms-${ZC_FORM_SUFFIX}`,
  ).then((res) => res.json())
  const errors: Record<string, HTMLSpanElement> = {}

  for (const step of data.steps) {
    for (const field of step.fields) {
      if (requiredFields.some(({ fieldName }) => fieldName === field.name)) {
        field.required = true
      }

      if (field.inputType === 'select') {
      } else if (field.inputType === 'textarea') {
        const { field: fieldEl, error } = textareaField(field)
        fields.push(fieldEl)
        errors[field.name] = error
      } else if (field.type === 'Checkbox' || field.type === 'PrivacyStatement') {
        const { field: fieldEl, error } = checkboxField(field)
        fields.push(fieldEl)
        errors[field.name] = error
      } else if (field.type === 'Description') {
        const { field: fieldEl } = descriptionField({ html: field.description, name: field.name })
        fields.push(fieldEl)
      } else if (field.type === 'File') {
        const { field: fieldEl, error } = fileField(field)
        fields.push(fieldEl)
        errors[field.name] = error
      } else if (field.type === 'Radio') {
        const { field: fieldEl, error } = radioField({
          ...field,
          choices: field.options.map((option: any, i: number) => ({
            value: option.key,
            label: option.label,
            selected: i === 0,
          })),
        })
        fields.push(fieldEl)
        errors[field.name] = error
      } else {
        const { field: fieldEl, error } = textField(field)
        fields.push(fieldEl)
        errors[field.name] = error
      }
    }
  }

  if (customFields.length) {
    for (const { fieldName } of customFields) {
      const index = fields.findIndex((field) => field.getAttribute('data-name') === fieldName)

      if (index > -1) {
        filteredFields.push(fields[index])
        fields.splice(index, 1)
      }
    }
  } else {
    filteredFields.push(...fields)
  }

  const form = h(
    'form',
    { class: 'vbzc-vacancy-form' },
    {
      children: [
        ...filteredFields,
        h('button', { class: 'vbzc-button vbzc-button-filled', type: 'submit' }, { text: ZC_SUBMIT_LABEL }),
      ],
    },
  ) as HTMLFormElement

  form.addEventListener('submit', async (event) => {
    event.preventDefault()

    const _formData = new FormData(form)
    const formData = new FormData()
    const responses: { type: string; name: string; response: any }[] = []

    for (const step of data.steps) {
      for (const field of step.fields) {
        if (filteredFields.some((filteredField) => filteredField.getAttribute('data-name') === field.name)) {
          let value = _formData.get(field.name)

          if (field.name === 'mobilePhoneNumber' && typeof value === 'string' && value.trim()) {
            value = String(value ?? '').replace(/[^0-9+]/g, '')

            if (!value.startsWith('00') && !value.startsWith('+')) {
              value = ZC_DEFAULT_COUNTRY_CODE + value
            } else if (value.startsWith('0') && !value.startsWith('00')) {
              value = ZC_DEFAULT_COUNTRY_CODE + value.slice(1)
            }
          }

          if (field.type === 'File') {
            const file = value as File
            formData.append(field.name, file)
            responses.push({ type: field.type, name: field.name, response: file.name })
          } else if (field.type === 'Checkbox') {
            responses.push({ type: field.type, name: field.name, response: value === 'on' })
          } else {
            responses.push({ type: field.type, name: field.name, response: value })
          }

          if (
            field.required &&
            (!value ||
              (typeof value === 'string' && !value.trim()) ||
              (value && typeof value === 'object' && !value.size))
          ) {
            errors[field.name].textContent = ZC_REQUIRED_MESSAGE
          } else if (field.validation && typeof value === 'string' && !new RegExp(field.validation).test(value)) {
            errors[field.name].textContent = ZC_VALIDATION_MESSAGE
          } else if (errors[field.name]) {
            errors[field.name].textContent = ''
          }
        }
      }
    }

    formData.append(
      'resource',
      JSON.stringify({
        application: {
          vacancyId,
          applicationFormId: `ApplicationForms-${ZC_FORM_SUFFIX}`,
          responses,
        },
      }),
    )

    const firstError = Object.values(errors).find((error) => error.textContent)
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    await fetch(`https://${ZC_HANDLE}.cockpit.recruitnow.${ZC_DOMAIN}/jobsite/api/applications`, {
      method: 'POST',
      body: formData,
    })
      .then(() => {
        form.innerHTML = `<p>${ZC_SUCCESS_MESSAGE}</p>`
      })
      .catch((error) => {
        console.log(error)
      })
  })

  return form
}
